import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Accordion } from '@vio/atlas/Accordion';
import { Icon } from '@vio/atlas/Icon';
import { SelectionCard } from '@vio/atlas/SelectionCard';
import { SkeletonLoader } from '@vio/atlas/SkeletonLoader';
import { FilterUrlParam } from 'modules/search/types';
import { getFacetsSortedAlphabetically } from 'utils/facilities';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { FacetFilterProps } from '../FacetFilter';
import { FacetFilterList } from '../FacetFilter/FacetFilterList/FacetFilterList';
import { FacetGroup, FacetGroupsType } from '../FacetFilter/types';
import { getValidFacetGroups, getValidFacets } from '../utils';
interface Props extends FacetFilterProps {
  isPopover?: boolean;
}
const SelectionCardWrapper = /*#__PURE__*/styled('div')({
  name: "SelectionCardWrapper",
  class: "s18ltg2i",
  propsAsIs: false
});
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "wmzqm8f",
  propsAsIs: false
});
const FiltersWrapper = /*#__PURE__*/styled('div')({
  name: "FiltersWrapper",
  class: "f1i4q89c",
  propsAsIs: false
});
const FilterGroupItemWrapper = /*#__PURE__*/styled('div')({
  name: "FilterGroupItemWrapper",
  class: "fg1fj02",
  propsAsIs: false
});
const PropertyTypeFilterComponent: React.FC<Props> = ({
  isPopover,
  facetGroups,
  appliedFilters,
  facets,
  filters,
  getLabel,
  onChange,
  searchParamName,
  columns,
  contractedMessage,
  limit,
  showMoreRenderType,
  setMultiFilters,
  removeMultiFilters
}) => {
  const {
    formatMessage
  } = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const validFacets = useMemo(() => getValidFacets(facets, appliedFilters), [facets, appliedFilters]);
  const availableFacetGroup: FacetGroup = useMemo(() => getValidFacetGroups(validFacets, facetGroups), [validFacets, facetGroups]);
  const [selectedGroup, setSelectedGroup] = useState<Partial<Record<FacetGroupsType, boolean>>>({
    hotels: false,
    vacationRentals: false
  });

  /** Handles selecting/deselecting a facet group */
  const handleSelectGroup = (type: FacetGroupsType) => {
    setSelectedGroup(prevSelection => {
      const facetIds = availableFacetGroup[type].map(facet => facet.id);
      prevSelection[type] ? removeMultiFilters(searchParamName, facetIds) : setMultiFilters(searchParamName, facetIds);
      return {
        ...prevSelection,
        [type]: !prevSelection[type]
      };
    });
  };

  /** Handles checking if a facet group is selected based on applied filters */
  const handleCheckSelectionCard = useCallback((type: FacetGroupsType, filterValues: FilterUrlParam) => {
    if (Array.isArray(filterValues)) {
      let hasFilter = false;
      filterValues.every(filter => {
        const isHotelGroupSelected = availableFacetGroup[type].find(facet => facet.id === filter);
        if (isHotelGroupSelected) {
          hasFilter = true;
          setSelectedGroup(prev => ({
            ...prev,
            [type]: true
          }));
          return false;
        }
        return true;
      });
      if (!hasFilter) setSelectedGroup(prev => ({
        ...prev,
        [type]: false
      }));
    } else if (filterValues) {
      const isHotelGroupSelected = availableFacetGroup[type].find(facet => facet.id === filterValues);
      if (isHotelGroupSelected) {
        setSelectedGroup(prev => ({
          ...prev,
          [type]: true
        }));
      } else {
        setSelectedGroup(prev => ({
          ...prev,
          [type]: false
        }));
      }
    }
  }, [availableFacetGroup]);

  /** Deselect selection cards when there are no filters */
  useEffect(() => {
    if (Array.isArray(filters) && filters.length === 0 || !filters) {
      setTimeout(() => {
        setSelectedGroup({
          hotels: false,
          vacationRentals: false
        });
      }, 10);
    }
  }, [filters]);

  /**  Checking if a facet group is selected based on applied filters */
  useEffect(() => {
    handleCheckSelectionCard('hotels', filters);
    handleCheckSelectionCard('vacationRentals', filters);
  }, [filters, availableFacetGroup, handleCheckSelectionCard]);

  // Showing SkeletonLoader
  if (validFacets?.length === 0) return <Wrapper className={cx(isPopover && '__isPopover')}>
        <SkeletonLoader height={86} marginBottom={24} />
        <SkeletonLoader height={86} />
      </Wrapper>;
  return <Wrapper className={cx(isPopover && '__isPopover')}>
      <FiltersWrapper>
        {facetGroups.filter(group => availableFacetGroup[group.type].length > 0).map((group, index) => <FilterGroupItemWrapper key={group.type} className={cx(isExpanded && index + 1 !== facetGroups.length && '__hasPadding')}>
              <div>
                <SelectionCardWrapper>
                  <SelectionCard name={group.type} selected={selectedGroup[group.type]} size="md" onChange={() => handleSelectGroup(group.type)} // We need to use this selection card as a checkbox not a radio button
            type="checkbox" icon={<Icon name={group.header?.groupIcon} size="md" />} title={formatMessage(group.header?.title)}>
                    {formatMessage(group.header?.description)}
                  </SelectionCard>
                </SelectionCardWrapper>
                {isExpanded && <FacetFilterList facets={getFacetsSortedAlphabetically(availableFacetGroup[group.type], getLabel as (T: string) => string)} filters={filters} searchParamName={searchParamName} getLabel={getLabel} columns={columns} contractedMessage={contractedMessage} onChange={onChange} limit={limit} showMoreRenderType={showMoreRenderType} />}
              </div>
            </FilterGroupItemWrapper>)}
      </FiltersWrapper>

      <Accordion isBottomAligned size="sm" header={isExpanded ? <FormattedMessageWrapper id="showLess" defaultMessage="Show less" /> : <FormattedMessageWrapper id="showMore" defaultMessage="Show more" />} onToggle={() => setIsExpanded(state => !state)}>
        <></>
      </Accordion>
    </Wrapper>;
};
export const PropertyTypeFilter = React.memo(PropertyTypeFilterComponent);

require("./PropertyTypeFilter.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PropertyTypeFilter.tsx");